/**
 * MONSTER TOWN — TOWN ECONOMY PROPS. OWNED BY THE VENUE/ECONOMY CREW. DATA ONLY.
 *
 * The pieces that make coins a thing you find and spend out in the world rather
 * than a number in a corner: the job board, the coins lying in the grass, the
 * shop shelf that sells whatever any crew has registered, and two bits of cosy
 * decor so a shop is not a bare box.
 *
 * Split out of `venue.ts` only to keep both files under the 500-line house
 * rule; `venue.ts` re-exports everything here, so nothing else had to change.
 */

import { FOOT_1x1, FOOT_2x1 } from '../core/grid'
import { coinRule } from '../core/economy'
import { definePlaceable } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import { ball, boxOn, cylinder, group, inked } from '../render/kit'
import { findCoinArt, jobBoardArt } from '../venue/fixtures'
import { shopCounterArt, shopShelfArt } from '../venue/shopArt'

const id = (s: string): PlaceableId => s as PlaceableId

// ---------------------------------------------------------------------------
// town economy props
// ---------------------------------------------------------------------------

export const jobBoard = definePlaceable<{ jobs: number }>({
  id: id('town_job_board'),
  devName: 'Job board',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['economy', 'starter'],
  initialState: () => ({ jobs: 0 }),
  buildArt: (ctx) => jobBoardArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'flap', seconds: 0.6, target: 'board' },
      { kind: 'particles', effect: 'coinSparkle', count: 7 },
      { kind: 'spawn', what: 'town:job' },
      { kind: 'sound', cue: 'paper' },
      { kind: 'state', patch: { jobs: ctx.state.jobs + 1 } },
    ],
    cooldownMs: 500,
  }),
})

export const findCoin = definePlaceable<{ taken: boolean }>({
  id: id('town_find_coin'),
  devName: 'Coin in the grass',
  category: 'townProp',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  tags: ['economy'],
  initialState: () => ({ taken: false }),
  buildArt: (ctx) => findCoinArt(ctx),
  interact: (ctx) => {
    const rule = coinRule('town.find')
    const amount = rule.base + Math.floor(ctx.rng() * ((rule.bonus ?? 0) + 1))
    return {
      feedback: [
        { kind: 'anim', clip: 'spinUp', seconds: 0.5, target: 'spinner' },
        { kind: 'coins', amount, source: 'town.find' },
        { kind: 'particles', effect: 'coinSparkle', count: 8 },
        { kind: 'spawn', what: 'town:coinTaken' },
        { kind: 'sound', cue: 'coin' },
        { kind: 'state', patch: { taken: true } },
      ],
      cooldownMs: 400,
    }
  },
})

/** A shelf in the town shop. What it sells lives in its instance state. */
export const shopPlinth = definePlaceable<{ offer: string; cost: number; owned: boolean }>({
  id: id('shop_plinth'),
  devName: 'Shop shelf',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['shop'],
  initialState: () => ({ offer: 'block_wood', cost: 0, owned: false }),
  buildArt: (ctx) => shopShelfArt(ctx),
  interact: () => ({
    feedback: [
      { kind: 'anim', clip: 'lift', seconds: 0.45, target: 'thumb' },
      { kind: 'particles', effect: 'coinSparkle', count: 6 },
      { kind: 'spawn', what: 'shop:buy' },
      { kind: 'sound', cue: 'shop_tap' },
    ],
    cooldownMs: 350,
  }),
})

/**
 * THE SHOP COUNTER. A shop with nothing but shelves in it is a storeroom — the
 * captured frame of this room was twelve identical plinths and no counter, no
 * signage and nobody serving. The shopkeeper stands behind this (see
 * `installShopKeeper`), and tapping it is a hello, never a purchase.
 */
export const shopCounter = definePlaceable<{ hellos: number }>({
  id: id('shop_counter'),
  devName: 'Shop counter',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['wallLilac', 'wallMint', 'wallCream'],
  tags: ['shop'],
  initialState: () => ({ hellos: 0 }),
  buildArt: (ctx) => shopCounterArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'ring', seconds: 0.45, target: 'till' },
      { kind: 'particles', effect: 'coinSparkle', count: 6 },
      { kind: 'sound', cue: 'bell_ding' },
      { kind: 'ui', panel: 'shop' },
      { kind: 'state', patch: { hellos: ctx.state.hellos + 1 } },
    ],
    cooldownMs: 400,
  }),
})

/** A cosy potted plant so the shop and the venue are not bare boxes. */
export const venuePlant = definePlaceable<{ shakes: number }>({
  id: id('venue_plant'),
  devName: 'Shop plant',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 35,
  stackable: false,
  walkable: false,
  tints: ['leaf', 'leafLight', 'leafDark'],
  tags: ['venue', 'cosy'],
  initialState: () => ({ shakes: 0 }),
  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('venuePlant')
    const pot = cylinder(t * 0.24, t * 0.3, { color: 'roofRed', seg: 12, name: 'pot' })
    g.add(inked(pot))
    const leaves = group('leaves')
    leaves.position.y = t * 0.3
    const c = ctx.tint ?? 'leaf'
    leaves.add(ball(t * 0.26, { color: c, y: t * 0.2, seg: 10 }))
    leaves.add(ball(t * 0.18, { color: 'leafLight', x: t * 0.16, y: t * 0.42, seg: 9 }))
    leaves.add(ball(t * 0.16, { color: c, x: -t * 0.16, y: t * 0.38, seg: 9 }))
    g.add(leaves)
    return g
  },
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'shake', seconds: 0.5, target: 'leaves' },
      { kind: 'particles', effect: 'petals', count: 6 },
      { kind: 'sound', cue: 'leaves' },
      { kind: 'state', patch: { shakes: ctx.state.shakes + 1 } },
    ],
  }),
})

/** A rug that makes the shop floor feel like a room, not a grid. */
export const venueRug = definePlaceable<{ pats: number }>({
  id: id('venue_rug'),
  devName: 'Shop rug',
  category: 'decor',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 45,
  stackable: false,
  walkable: true,
  tints: ['floorRug', 'fabricAlt', 'wallLilac', 'cushion'],
  tags: ['venue', 'cosy'],
  initialState: () => ({ pats: 0 }),
  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('venueRug')
    g.add(boxOn(t * 1.9, t * 0.04, t * 0.9, { color: ctx.tint ?? 'floorRug', name: 'rug' }))
    g.add(boxOn(t * 1.5, t * 0.045, t * 0.6, { color: 'uiPanelAlt', y: t * 0.04 }))
    g.add(boxOn(t * 0.9, t * 0.05, t * 0.35, { color: ctx.tint ?? 'floorRug', y: t * 0.08 }))
    return g
  },
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'ripple', seconds: 0.5, target: 'rug' },
      { kind: 'particles', effect: 'dustPuff', count: 5 },
      { kind: 'monster', action: 'bounce', seconds: 0.6 },
      { kind: 'sound', cue: 'soft_thump' },
      { kind: 'state', patch: { pats: ctx.state.pats + 1 } },
    ],
  }),
})


/** Registered through `venue.ts`, which spreads this into `VENUE_CONTENT`. */
export const TOWN_ECONOMY_CONTENT = [jobBoard, findCoin, shopPlinth, shopCounter, venuePlant, venueRug]

/** Never chargeable: the job board, the coins you find, and the shop's shelves. */
export const TOWN_ECONOMY_FREE_IDS: PlaceableId[] = [jobBoard.id, findCoin.id, shopPlinth.id, shopCounter.id]
